import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Refresh, Search, Download, ArrowLeft } from "@/components/icons";

import { fetchEcache } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Stat } from "@/components/Stat";
import { DataTable } from "@/components/ui/data-table";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { DataTableShell } from "@/components/ui/data-table-shell";
import { formatDuration, formatEnergy, formatNumber } from "@voltie/localization-web";
import { useCatalog } from "@/app/localization";

const INTERVALS_PER_DAY = 96; // 15-min intervals in 24h

function intervalToTime(idx: number): string {
  const h = Math.floor((idx * 15) / 60);
  const m = (idx * 15) % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** A loosely typed API number: formatted when finite, shown as sent otherwise (never guessed). */
function finite(v: unknown, format: (n: number) => string): string {
  const n = Number(v);
  return Number.isFinite(n) ? format(n) : String(v);
}

function valueColor(val: number): string {
  if (val <= 0) return "bg-card";
  if (val < 0.5) return "bg-success/30";
  if (val < 2) return "bg-success/60";
  if (val < 5) return "bg-warning/60";
  return "bg-error/60";
}

export function EcachePage() {
  const { t, catalog } = useCatalog();
  const [selectedUnit, setSelectedUnit] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 100;

  const ecacheColumns = useMemo<ColumnDef<any, any>[]>(() => [
    {
      id: "unit_id",
      header: t("operator.units.column_unit_id"),
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.unit_id || row.original.id || t("operator.ops.ecache.unit_unknown")}</span>,
    },
    {
      accessorKey: "total_energy",
      header: t("operator.ops.ecache.total_energy"),
      cell: ({ getValue }) => getValue() != null ? finite(getValue(), (n) => formatEnergy(n, "kWh", "summary")) : "—",
    },
    {
      accessorKey: "peak",
      header: t("operator.ops.ecache.peak"),
      cell: ({ getValue }) => getValue() != null ? finite(getValue(), (n) => formatNumber(n, 2)) : "—",
    },
    {
      accessorKey: "active_intervals",
      header: t("operator.ops.ecache.intervals_active"),
      cell: ({ getValue }) => getValue() ?? "—",
    },
    {
      id: "action",
      header: () => <span className="text-right block">{t("operator.ops.ecache.action")}</span>,
      cell: ({ row }) => {
        const uid = row.original.unit_id || row.original.id || "unknown";
        return (
          <div className="text-right">
            <button className="text-sm text-primary underline-offset-4 hover:underline" onClick={(e) => { e.stopPropagation(); setSelectedUnit(uid); }}>
              {t("operator.ops.ecache.view_detail")}
            </button>
          </div>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [catalog]);

  const summaryQ = useQuery({
    queryKey: ["ecache", "summary"],
    queryFn: () => fetchEcache(),
    refetchInterval: 30_000,
  });

  const detailQ = useQuery({
    queryKey: ["ecache", "detail", selectedUnit],
    queryFn: () => fetchEcache(selectedUnit!),
    enabled: !!selectedUnit,
    refetchInterval: 30_000,
  });

  function exportCsv() {
    if (!detailQ.data) return;
    const intervals = detailQ.data.intervals || detailQ.data.data || [];
    const csv = ["Interval,Time,Value", ...intervals.map((v: number, i: number) => `${i},${intervalToTime(i)},${v}`)].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ecache_${selectedUnit}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Summary view
  if (!selectedUnit) {
    const data = summaryQ.data;
    const units: any[] = data?.units || data?.summary || [];
    const stats = data?.stats || data || {};

    const filtered = units.filter((u: any) => {
      const id = u.unit_id || u.id || "";
      return id.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const paged = filtered.slice(page * pageSize, (page + 1) * pageSize);

    return (
      <div className="space-y-5 pt-[86px]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{t("operator.nav.ecache")}</h1>
            <div className="text-sm opacity-70">{t("operator.ops.ecache.subtitle")}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => summaryQ.refetch()}>
            <Refresh className="h-4 w-4" /> {t("common.action.refresh")}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid gap-3 md:grid-cols-3">
          <Stat title={t("operator.ops.ecache.total_units")} value={stats.total_units ?? units.length ?? 0} />
          <Stat title={t("operator.ops.ecache.units_with_data")} value={stats.units_with_data ?? "—"} />
          <Stat title={t("operator.ops.auto_refresh")} value={formatDuration(30, "seconds")} />
        </div>

        {/* Search */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-1 max-w-md">
              <Button variant="ghost" size="sm" aria-label={t("operator.ops.ecache.search_label")}><Search className="h-4 w-4" /></Button>
              <input
                className="h-8 w-full rounded-4xl border border-input bg-input/30 px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                placeholder={t("operator.ops.ecache.search_placeholder")}
                aria-label={t("operator.ops.ecache.search_label")}
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              />
            </div>
          </CardContent>
        </Card>

        {summaryQ.isError ? (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">{t("operator.ops.ecache.load_failed")}</div>
        ) : (
          <>
            <DataTableShell>
              <DataTable
                columns={ecacheColumns}
                data={paged}
                isLoading={summaryQ.isLoading}
                tableClassName="text-xs"
                emptyMessage={t("operator.ops.ecache.empty")}
              />
            </DataTableShell>

            {/* Pagination */}
            {filtered.length > pageSize && (
              <div className="flex justify-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>{t("operator.ops.page_prev")}</Button>
                <Button variant="ghost" size="sm">{t("operator.ops.page_of", { page: page + 1, pages: Math.ceil(filtered.length / pageSize) })}</Button>
                <Button variant="outline" size="sm" disabled={(page + 1) * pageSize >= filtered.length} onClick={() => setPage(page + 1)}>{t("operator.ops.page_next")}</Button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // Detail view
  const detailData = detailQ.data;
  const intervals: number[] = detailData?.intervals || detailData?.data || [];

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Button variant="ghost" size="sm" className="gap-1 mb-2" onClick={() => setSelectedUnit(null)}>
            <ArrowLeft className="h-4 w-4" /> {t("operator.ops.ecache.back")}
          </Button>
          <h1 className="text-2xl font-semibold">{t("operator.ops.ecache.detail_title", { unitId: selectedUnit })}</h1>
          <div className="text-sm opacity-70">{t("operator.ops.ecache.detail_subtitle")}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={exportCsv} disabled={!intervals.length}>
            <Download className="h-4 w-4" /> {t("operator.ops.ecache.csv")}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => detailQ.refetch()}>
            <Refresh className="h-4 w-4" /> {t("common.action.refresh")}
          </Button>
        </div>
      </div>

      {detailQ.isLoading ? (
        <div className="flex justify-center p-12">
          <span role="status" aria-label={t("common.state.loading")} className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></span>
        </div>
      ) : detailQ.isError ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">{t("operator.ops.ecache.detail_load_failed")}</div>
      ) : (
        <Card>
          <CardContent>
            <CardTitle className="text-sm">{t("operator.ops.ecache.heatmap_title", { count: INTERVALS_PER_DAY })}</CardTitle>
            <div className="grid grid-cols-12 gap-1 mt-2">
              {Array.from({ length: INTERVALS_PER_DAY }).map((_, i) => {
                const val = intervals[i] ?? 0;
                return (
                  <div
                    key={i}
                    className={`rounded h-8 flex items-center justify-center text-[10px] font-mono ${valueColor(val)}`}
                    title={t("operator.ops.ecache.interval_tooltip", { time: intervalToTime(i), energy: finite(val, (n) => formatEnergy(n, "kWh", "meter")) })}
                  >
                    {i % 4 === 0 ? intervalToTime(i).slice(0, 2) : ""}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-4 flex items-center gap-3 text-xs">
              <span>{t("operator.ops.ecache.legend")}</span>
              <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-card" /> 0</span>
              <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-success/30" /> {t("operator.ops.ecache.legend_low")}</span>
              <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-success/60" /> {t("operator.ops.ecache.legend_medium")}</span>
              <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-warning/60" /> {t("operator.ops.ecache.legend_high")}</span>
              <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-error/60" /> {t("operator.ops.ecache.legend_peak")}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
